import { useEffect, useState } from 'react';
import type { WorkAction, WorkItemListItem, WorkPhase } from '@vwt/contracts';
import { toast } from '@vwt/ui';
import { api, newIdempotencyKey, queryClient } from '../../lib/api.js';
import { errorMessage } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { TransitionDialog } from './work-detail.js';

/** Phases people move work between by hand (canceling has its own action in the item). */
export const MOVABLE_PHASES: WorkPhase[] = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'INTEGRATED', 'DONE'];

/** Maps a phase move to the guarded transition the server will evaluate. */
export function actionFor(from: WorkPhase, to: WorkPhase, policy: string): WorkAction | null {
  if (from === to) return null;
  if (from === 'TODO' && to === 'IN_PROGRESS') return 'start';
  if (from === 'IN_PROGRESS' && to === 'IN_REVIEW') return 'request_review';
  if (from === 'IN_REVIEW' && to === 'IN_PROGRESS') return 'request_changes';
  if ((from === 'DONE' || from === 'CANCELED' || from === 'EXPIRED') && to === 'IN_PROGRESS') return 'reopen';
  if (to === 'DONE' && policy === 'DELIVERABLE') return 'complete_deliverable';
  if (to === 'DONE' && policy === 'REVIEWED_DELIVERABLE') return 'approve_deliverable';
  // Production work (or a jump past the usual steps) can still be closed, recorded as "No evidence".
  if (to === 'DONE' && from !== 'DONE') return 'close_without_evidence';
  return null;
}

/** Phases an item can be moved to from where it is (for menus). */
export function reachablePhases(item: Pick<WorkItemListItem, 'phase' | 'completionPolicy'>): WorkPhase[] {
  return MOVABLE_PHASES.filter((p) => actionFor(item.phase, p, item.completionPolicy));
}

/**
 * Moves work between phases through the guarded transitions. Moves that need more input (a reason,
 * an outcome, a reviewer) open the transition dialog; nothing fakes production completion.
 */
export function usePhaseMove() {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<{ id: string; action: WorkAction } | null>(null);
  const move = async (item: WorkItemListItem, to: WorkPhase) => {
    const action = actionFor(item.phase, to, item.completionPolicy);
    if (!action) {
      toast.error(t('work.board.dropRejected', { reason: item.completionPolicy === 'PRODUCTION' && (to === 'DONE' || to === 'INTEGRATED') ? t('work.board.productionHint') : `${t(`status.phase.${item.phase}`)} → ${t(`status.phase.${to}`)}` }));
      return;
    }
    // A bug moved to Done may need its cause; the dialog loads the item and asks only when it is missing.
    if (['request_changes', 'reopen', 'complete_deliverable', 'cancel', 'close_without_evidence'].includes(action) || (action === 'request_review' && item.completionPolicy === 'REVIEWED_DELIVERABLE') || (item.type === 'BUG' && to === 'DONE')) {
      setDialog({ id: item.id, action });
      return;
    }
    try {
      await api.call('transitionWorkItem', { params: { id: item.id }, idempotencyKey: newIdempotencyKey(), body: { expectedVersion: item.version, action } });
      await queryClient.invalidateQueries({ queryKey: ['listWorkItems'] });
    } catch (error) {
      toast.error(t('work.board.dropRejected', { reason: errorMessage(error) }));
    }
  };
  const element = dialog ? <DialogLoader id={dialog.id} action={dialog.action} onClose={() => setDialog(null)} /> : null;
  return { move, dialog: element };
}

function DialogLoader({ id, action, onClose }: { id: string; action: WorkAction; onClose: () => void }) {
  const [item, setItem] = useState<Awaited<ReturnType<typeof api.call<'getWorkItem'>>> | null>(null);
  useEffect(() => {
    let active = true;
    void api.call('getWorkItem', { params: { id } }).then((result) => active && setItem(result));
    return () => {
      active = false;
    };
  }, [id]);
  return item ? <TransitionDialog item={item.data} action={action} onClose={onClose} /> : null;
}
